<template>
  <div class="visitor-assignment">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>咨询师-来访者关联管理</span>
        <el-button 
          style="float: right; padding: 3px 0" 
          type="text" 
          @click="fetchData"
          :loading="loading"
        >刷新数据</el-button>
      </div>

      <div class="control-panel">
        <span class="label">选择咨询师：</span>
        <el-select 
          v-model="selectedCounselor" 
          placeholder="请选择咨询师" 
          @change="handleCounselorChange"
          filterable
          clearable
          style="width: 300px"
        >
          <el-option
            v-for="user in counselors"
            :key="user.id"
            :label="user.username + (user.name ? ' (' + user.name + ')' : '')"
            :value="user.id">
          </el-option>
        </el-select>
        
        <span class="admin-hint">
          <i class="el-icon-info"></i> 管理员/超级管理员默认拥有所有权限，无需在此配置
        </span>

        <el-button 
          type="primary" 
          @click="saveAssignment" 
          :disabled="!selectedCounselor"
          :loading="saving"
          style="margin-left: 20px"
        >保存关联关系</el-button>
      </div>

      <div class="transfer-container" v-loading="loadingAssignment">
        <el-transfer
          v-model="transferValue"
          :data="transferData"
          :titles="['可选来访者', '已关联 (包含创建者)']"
          :button-texts="['移除', '添加']"
          :format="{
            noChecked: '${total}',
            hasChecked: '${checked}/${total}'
          }"
          filterable
          filter-placeholder="请输入来访者姓名搜索"
          class="custom-transfer"
        >
          <span slot-scope="{ option }">
            {{ option.label }}
            <el-tag v-if="option.isCreator" size="mini" type="info" effect="plain" style="margin-left: 5px">被创建</el-tag>
          </span>
        </el-transfer>
      </div>
      
      <div class="help-text">
        <i class="el-icon-info"></i> 说明：
        <ul>
          <li>左侧列表显示所有可选的来访者。</li>
          <li>右侧列表显示该咨询师管理的来访者（包括协作的和自己创建的）。</li>
          <li>标有“被创建”标签的来访者由该咨询师创建，无法移除关联。</li>
          <li>选中并移动来访者即可建立或取消协作关系，操作后请点击“保存关联关系”按钮生效。</li>
        </ul>
      </div>
    </el-card>
  </div>
</template>

<script>
import { getUsers } from '@/api/auth'
import { getVisitors, getVisitorAssignment, assignVisitors } from '@/api/visitors'
import { notifyApiError } from '@/utils/notifyApiError'

export default {
  name: 'VisitorAssignment',
  data() {
    return {
      counselors: [],
      allVisitors: [],
      selectedCounselor: null,
      transferData: [],
      transferValue: [],
      loading: false,
      loadingAssignment: false,
      saving: false,
      createdIds: [], // Store IDs created by the selected counselor
      collaboratingIds: [], // Store IDs collaborating with the selected counselor
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.loading = true
      try {
        // Fetch counselors (users)
        // Request large page size to get all users if possible, or assume pagination
        const usersRes = await getUsers({ page_size: 1000 })
        const allUsers = usersRes.data.results || []
        
        // Filter out admins and superusers because they already have access to everything
        this.counselors = allUsers.filter(user => user.role !== 'admin' && !user.is_superuser)

        // Fetch all visitors
        // Request large page size
        const visitorsRes = await getVisitors({ page_size: 10000 })
        this.allVisitors = visitorsRes.data.results || []

        // If a counselor is already selected, refresh their assignment
        if (this.selectedCounselor) {
          this.handleCounselorChange(this.selectedCounselor)
        } else {
          // Initialize transfer data (empty state)
          this.updateTransferData()
        }
      } catch (error) {
        console.error('Fetch data error:', error)
        notifyApiError(error, '获取数据失败')
      } finally {
        this.loading = false
      }
    },
    async handleCounselorChange(counselorId) {
      if (!counselorId) {
        this.createdIds = []
        this.collaboratingIds = []
        this.updateTransferData()
        return
      }
      
      this.loadingAssignment = true
      try {
        const res = await getVisitorAssignment(counselorId)
        this.createdIds = res.data.created_ids || []
        this.collaboratingIds = res.data.collaborating_ids || []
        
        this.updateTransferData()
      } catch (error) {
        console.error('Fetch assignment error:', error)
        notifyApiError(error, '获取关联关系失败')
      } finally {
        this.loadingAssignment = false
      }
    },
    updateTransferData() {
      const data = []
      const value = []
      
      // 如果没有选择咨询师，禁用所有选项
      const isGlobalDisabled = !this.selectedCounselor
      
      this.allVisitors.forEach(visitor => {
        const isCreator = this.createdIds.includes(visitor.id)
        const isCollaborator = this.collaboratingIds.includes(visitor.id)
        
        data.push({
          key: visitor.id,
          label: `${visitor.name} (${visitor.phone || '无电话'})`,
          disabled: isGlobalDisabled || isCreator, // Disable if no counselor selected OR is creator
          isCreator: isCreator
        })
        
        // If they are creator or collaborator, they should be on the right side
        if (isCreator || isCollaborator) {
          value.push(visitor.id)
        }
      })
      
      this.transferData = data
      this.transferValue = value
    },
    async saveAssignment() {
      if (!this.selectedCounselor) return
      
      this.saving = true
      try {
        // Filter out created_ids from the transferValue to get only collaborators
        const collaboratorIds = this.transferValue.filter(id => !this.createdIds.includes(id))
        
        await assignVisitors({
          counselor_id: this.selectedCounselor,
          visitor_ids: collaboratorIds
        })
        
        this.$message.success('关联关系保存成功')
        
        // Refresh data to be sure
        this.handleCounselorChange(this.selectedCounselor)
        
      } catch (error) {
        console.error('Save assignment error:', error)
        notifyApiError(error, '保存失败')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.visitor-assignment {
  height: 100%;
}
.control-panel {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}
.label {
  font-weight: bold;
  margin-right: 10px;
}
.admin-hint {
  font-size: 12px;
  color: #909399;
  margin-left: 10px;
}
.transfer-container {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}
.custom-transfer {
  text-align: left;
  display: inline-block;
}
/* Deep selector to widen the transfer panels */
.custom-transfer ::v-deep .el-transfer-panel {
  width: 300px;
}

/* Dark mode overrides */
.custom-transfer ::v-deep .el-transfer-panel {
  background: var(--bg-card);
  border-color: var(--border-color);
}
.custom-transfer ::v-deep .el-transfer-panel__header {
  background: var(--el-table-header-bg); /* Need to check if this var exists or use generic */
  background: var(--bg-body); /* Fallback to body bg or similar */
  border-bottom-color: var(--border-color);
  color: var(--text-primary);
}
.custom-transfer ::v-deep .el-transfer-panel__header .el-checkbox__label {
  color: var(--text-primary);
}
.custom-transfer ::v-deep .el-transfer-panel__body {
  background: var(--bg-card);
}
.custom-transfer ::v-deep .el-transfer-panel__list.is-filterable {
  background: var(--bg-card);
}
.custom-transfer ::v-deep .el-transfer-panel__item.el-checkbox {
  color: var(--text-regular);
}
.custom-transfer ::v-deep .el-transfer-panel__item:hover {
  background-color: var(--el-table-hover-bg);
}
.custom-transfer ::v-deep .el-input__inner {
  background-color: var(--el-input-bg);
  border-color: var(--el-input-border);
  color: var(--el-input-text);
}

.help-text {
  margin-top: 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.6;
}
.help-text ul {
  padding-left: 20px;
  margin: 5px 0;
}
</style>
